import {useState} from 'react'
import {Button, Form, Modal, Spinner} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {CREATE_PDF_DOCUMENT_TEMPLATE_STR} from '../../../gql/mutations/pdfEnvelopesMutations'
import {useUploadFile} from '../../../hooks/useUploadFile'

interface Props {
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  onTemplateCreated?: (id: string) => void
}

export const ModalAddPdfTemplate = ({isOpen, onClose, refetch, onTemplateCreated}: Props) => {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [pdfFile, setPdfFile] = useState<File | null>(null)
  const [language, setLanguage] = useState('es')
  const [signerRoles, setSignerRoles] = useState([{role: 'client', label: 'Cliente'}])

  const [uploadAndCreate, {loading}] = useUploadFile<any>()

  const addRole = () => setSignerRoles((p) => [...p, {role: '', label: ''}])
  const removeRole = (i: number) => setSignerRoles((p) => p.filter((_, idx) => idx !== i))
  const updateRole = (i: number, key: 'role' | 'label', val: string) => {
    setSignerRoles((p) => p.map((r, idx) => (idx === i ? {...r, [key]: val} : r)))
  }

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error('Name is required')
      return
    }
    if (!pdfFile) {
      toast.error('Please select a PDF file')
      return
    }
    try {
      const formdata = new FormData()
      formdata.append('0', pdfFile, pdfFile.name)
      formdata.append('map', '{"0": ["variables.pdfFile"]}')
      formdata.append('operations', JSON.stringify({
        query: CREATE_PDF_DOCUMENT_TEMPLATE_STR,
        variables: {name, description, pdfFile: null, signerRoles: JSON.stringify(signerRoles), language},
      }))
      const result = await uploadAndCreate(formdata)
      const res = result?.pdfTemplateCreate
      if (res?.ok) {
        toast.success('Template created successfully')
        refetch()
        onTemplateCreated?.(res.templateId)
        onClose()
      } else {
        toast.error(res?.errors?.[0]?.message || 'Error creating template')
      }
    } catch (e: any) {
      toast.error(e.message)
    }
  }

  return (
    <Modal show={isOpen} onHide={onClose} size='lg' backdrop='static' keyboard={false}>
      <Modal.Header closeButton>
        <Modal.Title>New PDF Template</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className='mb-3'>
            <Form.Label className='fw-bold'>Name *</Form.Label>
            <Form.Control
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder='e.g. Dec Page Auto'
            />
          </Form.Group>
          <Form.Group className='mb-3'>
            <Form.Label className='fw-bold'>Description</Form.Label>
            <Form.Control
              as='textarea'
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Form.Group>
          <Form.Group className='mb-3'>
            <Form.Label className='fw-bold'>Signing Page Language</Form.Label>
            <Form.Select value={language} onChange={(e) => setLanguage(e.target.value)}>
              <option value='es'>Español</option>
              <option value='en'>English</option>
            </Form.Select>
            <Form.Text className='text-muted'>
              Language shown to signers on the signing page
            </Form.Text>
          </Form.Group>
          <Form.Group className='mb-4'>
            <Form.Label className='fw-bold'>PDF File *</Form.Label>
            <Form.Control
              type='file'
              accept='.pdf'
              onChange={(e) => {
                const f = (e.target as HTMLInputElement).files?.[0]
                if (f && f.size > 20 * 1024 * 1024) {
                  toast.warning('File is too large (max 20MB)')
                  return
                }
                setPdfFile(f ?? null)
              }}
            />
            <Form.Text className='text-muted'>Max 20MB</Form.Text>
          </Form.Group>

          <div className='mb-3'>
            <div className='d-flex align-items-center justify-content-between mb-2'>
              <Form.Label className='fw-bold mb-0'>Signer Roles</Form.Label>
              <Button size='sm' variant='outline-primary' onClick={addRole}>
                <i className='bi bi-plus' /> Add role
              </Button>
            </div>
            {signerRoles.map((r, i) => (
              <div key={i} className='d-flex gap-2 mb-2 align-items-center'>
                <Form.Control
                  placeholder='role key (e.g. client)'
                  value={r.role}
                  onChange={(e) => updateRole(i, 'role', e.target.value)}
                />
                <Form.Control
                  placeholder='Label (e.g. Client)'
                  value={r.label}
                  onChange={(e) => updateRole(i, 'label', e.target.value)}
                />
                {signerRoles.length > 1 && (
                  <Button size='sm' variant='outline-danger' onClick={() => removeRole(i)}>
                    <i className='bi bi-trash' />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={loading}>
          {loading ? <Spinner size='sm' animation='border' /> : 'Create Template'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
